import { stampMatrix } from '../brush/geometry';
import { PAINT_FILTER_IDS, TONE_RANGE_IDS, type PaintFilter, type ToneRange } from '../core/paintFilters';
import { rectAlignToPixels, rectExpand } from '../core/math';
import type { BlendMode, Point, Rect, RGBA } from '../core/types';
import type { Renderer } from '../gl/renderer';
import type { Texture } from '../gl/texture';
import type { DocumentResources } from './resources';
import type { StrokeTarget } from './strokeTarget';

/**
 * Scratch slots owned by the stroke pipeline. Document operations in `ops.ts`
 * use RGBA 1..3 and R8 1..2, and `strokeTarget.ts` owns RGBA 0 and R8 3, so
 * effects start above all of them.
 */
const FILTER_SOURCE = 4;
const FILTER_SCRATCH = 5;

/** The healing solve has the signed-field slots to itself. */
const HEAL_A = 0;
const HEAL_B = 1;

/**
 * Jacobi sweeps per neighbour distance in the healing solve. The solve runs once
 * per stroke, so the count is set for a clean result rather than for speed: with
 * too few sweeps the middle of a large patch keeps a visible cast of whatever it
 * was seeded with.
 */
const SWEEPS_PER_STEP = 16;

/**
 * Times the whole coarse-to-fine sequence repeats. Walking the scales twice
 * removes far more of the seed than the same number of sweeps at a single scale,
 * because each pass down the scales spreads the edge values over the region
 * again from the top.
 */
const CYCLES = 2;

export interface EffectContext {
  renderer: Renderer;
  resources: DocumentResources;
  width: number;
  height: number;
  target: StrokeTarget;
  selection: Texture | null;
  /**
   * Layer contentOffset: brush coverage/selection are document-space, the
   * surface is layer-local.
   */
  contentOffset: Point;
}

/** One dab, already jittered, as the appliers need to see it. */
export interface DabPlacement {
  x: number;
  y: number;
  width: number;
  height: number;
  angle: number;
  flow: number;
  /** Where the previous dab of this stroke landed, for path-dependent effects. */
  previous: Point | null;
  tip: Texture | null;
  inner: number;
  sharp: boolean;
}

/**
 * Turns the stroke's coverage into pixels. Colour tools rebuild the preview from
 * the untouched surface on every frame, which keeps a stroke idempotent; Smudge
 * works dab by dab because smearing depends on the order the dabs arrive in.
 */
export interface StrokeApplier {
  /** Called once, before the first dab. */
  begin?(): void;
  /** Rebuilds the live preview from the surface plus the coverage so far. */
  preview(coverage: Texture): void;
  /** Path-dependent effects do their work here instead. */
  dab?(placement: DabPlacement): void;
  /** Last pass before the preview is copied into the destination. */
  finalise?(coverage: Texture, dirty: Rect): void;
}

export interface PaintEffect {
  kind: 'paint' | 'erase';
  color: RGBA;
  blendMode: BlendMode;
  opacity: number;
}

export function paintApplier(ctx: EffectContext, effect: PaintEffect): StrokeApplier {
  const channel = ctx.target.colorChannel;
  return {
    preview(coverage) {
      // Painting one colour channel leaves the others as they are, so the preview
      // starts as a copy of the surface and only the chosen channel is written.
      if (channel !== null) {
        ctx.renderer.copy(ctx.target.preview.framebuffer, ctx.target.texture);
        ctx.renderer.setChannelWriteMask(channel);
      }
      ctx.renderer.applyStroke(
        ctx.target.preview.framebuffer,
        ctx.target.texture,
        coverage,
        ctx.selection,
        effect.color,
        effect.opacity,
        effect.kind === 'erase',
        effect.blendMode,
        { contentOffset: ctx.contentOffset },
      );
      if (channel !== null) ctx.renderer.setChannelWriteMask(null);
    },
  };
}

export interface FilterEffect {
  filter: PaintFilter;
  /** 0..1; Sponge passes a negative value to desaturate. */
  amount: number;
  range: ToneRange;
  /** Radius of the low-pass copy Blur and Sharpen work from. */
  blurRadius: number;
  /** Clone Stamp: frozen source and its offset in document pixels. */
  cloneSource?: Texture | null;
  offsetX?: number;
  offsetY?: number;
}

/**
 * Blur and Sharpen both need a low-pass copy of the surface. It is built once
 * per stroke: the radius follows the brush, not the pointer, so freezing it
 * costs nothing and saves a full-frame Gaussian on every pointer move.
 */
function lowPassCopy(ctx: EffectContext, radius: number): Texture {
  const source = ctx.resources.rgba(FILTER_SOURCE);
  const scratch = ctx.resources.rgba(FILTER_SCRATCH);
  const sigma = Math.max(0.6, radius / 2);
  const taps = Math.max(1, Math.min(48, Math.ceil(radius * 1.5)));

  ctx.renderer.blurColorPass(
    scratch.framebuffer,
    ctx.target.texture,
    1 / ctx.width,
    0,
    taps,
    sigma,
  );
  ctx.renderer.blurColorPass(source.framebuffer, scratch.texture, 0, 1 / ctx.height, taps, sigma);
  return source.texture;
}

/** Copy of the surface a Clone Stamp stroke reads from while it paints. */
export function freezeSurface(ctx: EffectContext, from: Texture): Texture {
  const frozen = ctx.resources.rgba(FILTER_SOURCE);
  ctx.renderer.copy(frozen.framebuffer, from);
  return frozen.texture;
}

export function filterApplier(ctx: EffectContext, effect: FilterEffect): StrokeApplier {
  const needsLowPass = effect.filter === 'blur' || effect.filter === 'sharpen';
  let source: Texture | null = effect.cloneSource ?? null;

  return {
    begin() {
      if (needsLowPass) source = lowPassCopy(ctx, effect.blurRadius);
    },
    preview(coverage) {
      ctx.renderer.applyStrokeFilter(
        ctx.target.preview.framebuffer,
        ctx.target.texture,
        coverage,
        ctx.selection,
        {
          type: PAINT_FILTER_IDS[effect.filter],
          amount: effect.amount,
          range: TONE_RANGE_IDS[effect.range],
          source,
          offsetX: effect.offsetX ?? 0,
          offsetY: effect.offsetY ?? 0,
          contentOffset: ctx.contentOffset,
        },
      );
    },
  };
}

/**
 * Smudge drags colour along the stroke. Each dab reads a copy of the region it
 * is about to change, so the pass never samples the texture it writes to.
 */
export function smudgeApplier(ctx: EffectContext, strength: number): StrokeApplier {
  const scratch = ctx.resources.rgba(FILTER_SCRATCH);

  return {
    begin() {
      ctx.renderer.copy(ctx.target.preview.framebuffer, ctx.target.texture);
    },
    preview() {
      // The working surface *is* the preview; rebuilding it would undo the smear.
    },
    dab(placement) {
      const previous = placement.previous;
      if (!previous) return;
      const pickupX = placement.x - previous.x;
      const pickupY = placement.y - previous.y;
      if (pickupX === 0 && pickupY === 0) return;

      const ox = ctx.contentOffset.x;
      const oy = ctx.contentOffset.y;
      const local = { x: placement.x - ox, y: placement.y - oy };
      const pad = Math.hypot(pickupX, pickupY) + 2;
      const region = stampMatrix(
        local,
        placement.width + pad * 2,
        placement.height + pad * 2,
        placement.angle,
        ctx.width,
        ctx.height,
      );
      ctx.renderer.copyRegion(scratch.framebuffer, ctx.target.preview.texture, region);

      const dabMatrix = stampMatrix(
        local,
        placement.width,
        placement.height,
        placement.angle,
        ctx.width,
        ctx.height,
      );
      ctx.renderer.smudgeDab(
        ctx.target.preview.framebuffer,
        scratch.texture,
        ctx.selection,
        dabMatrix,
        {
          inner: placement.inner,
          strength: strength * placement.flow,
          tip: placement.tip,
          sharp: placement.sharp,
          pickupX,
          pickupY,
          contentOffset: ctx.contentOffset,
        },
      );
    },
  };
}

export interface HealEffect {
  /** Source offset in document pixels, or null to interpolate the surroundings. */
  offsetX: number | null;
  offsetY: number | null;
}

/**
 * Spot Healing, the Healing Brush and Patch are one operation: solve for a
 * correction field that is smooth inside the region and matches the surroundings
 * on its edge, then add it to the source. With no source the solve interpolates
 * the surroundings, which is exactly what Spot Healing does.
 */
export function healRegion(
  ctx: EffectContext,
  mask: Texture,
  region: Rect,
  effect: HealEffect,
  destination = ctx.target.preview,
): void {
  const { renderer } = ctx;
  const base = ctx.target.texture;
  const useSource = effect.offsetX !== null && effect.offsetY !== null;
  const offsetX = effect.offsetX ?? 0;
  const offsetY = effect.offsetY ?? 0;
  const source = useSource ? base : null;

  const ox = ctx.contentOffset.x;
  const oy = ctx.contentOffset.y;
  const localRegion = {
    x: region.x - ox,
    y: region.y - oy,
    width: region.width,
    height: region.height,
  };
  const rect = rectAlignToPixels(rectExpand(localRegion, 2), ctx.width, ctx.height);
  if (rect.width <= 0 || rect.height <= 0) return;

  const a = ctx.resources.field(HEAL_A);
  const b = ctx.resources.field(HEAL_B);
  const full = stampMatrix(
    { x: ctx.width / 2, y: ctx.height / 2 },
    ctx.width,
    ctx.height,
    0,
    ctx.width,
    ctx.height,
  );
  const matrix = stampMatrix(
    { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 },
    rect.width,
    rect.height,
    0,
    ctx.width,
    ctx.height,
  );

  // Seeding the whole field with the boundary value lets the first sweeps use
  // long neighbour steps without reading uninitialised texels. The seed reads
  // `b` only to keep the sampler off the texture it renders into: sampling the
  // attachment of the current framebuffer is undefined, even when the shader
  // ignores the result.
  renderer.healJacobi(
    a.framebuffer,
    b.texture,
    base,
    source,
    mask,
    full,
    1,
    offsetX,
    offsetY,
    true,
    ctx.contentOffset,
  );
  renderer.copy(b.framebuffer, a.texture);

  let read = a;
  let write = b;
  const longest = Math.max(rect.width, rect.height);
  const coarsest = Math.max(1, 2 ** Math.floor(Math.log2(longest / 2)));
  for (let cycle = 0; cycle < CYCLES; cycle++) {
    for (let step = coarsest; step >= 1; step /= 2) {
      for (let sweep = 0; sweep < SWEEPS_PER_STEP; sweep++) {
        renderer.healJacobi(
          write.framebuffer,
          read.texture,
          base,
          source,
          mask,
          matrix,
          step,
          offsetX,
          offsetY,
          false,
          ctx.contentOffset,
        );
        const next = read;
        read = write;
        write = next;
      }
    }
  }

  renderer.copy(destination.framebuffer, base);
  renderer.healApply(
    destination.framebuffer,
    base,
    source,
    read.texture,
    mask,
    ctx.selection,
    matrix,
    offsetX,
    offsetY,
    ctx.contentOffset,
  );
}

/**
 * While the pointer is down the healed area is only marked, the same way
 * Photoshop shows a grey smear and heals on release: the solve is far too heavy
 * to run on every pointer move.
 */
export function healApplier(ctx: EffectContext, effect: HealEffect): StrokeApplier {
  const marker: RGBA = { r: 0.5, g: 0.5, b: 0.5, a: 1 };
  return {
    preview(coverage) {
      ctx.renderer.applyStroke(
        ctx.target.preview.framebuffer,
        ctx.target.texture,
        coverage,
        ctx.selection,
        marker,
        0.45,
        false,
        'normal',
        { contentOffset: ctx.contentOffset },
      );
    },
    finalise(coverage, dirty) {
      healRegion(ctx, coverage, dirty, effect);
    },
  };
}
